/**
 * Client-side structured logging.
 *
 * Why this exists: when a lawyer says "it broke", `console.log` in someone
 * else's browser is worthless. Every notable client event — API call, auth
 * transition, stream frame, background job, render crash — lands in an
 * in-memory ring buffer that the Diagnostics screen renders and can copy to
 * the clipboard, correlation ids and all. That transcript is what turns a
 * support ticket into a fix.
 *
 * Design:
 * - Bounded. A ring buffer of {@link BUFFER_SIZE} entries; a long session can
 *   never leak memory.
 * - Cheap. Below the active level nothing is allocated — arguments are not
 *   even evaluated when callers pass a thunk.
 * - Safe. Anything that looks like a credential is redacted before it is
 *   stored, so the buffer is safe to paste into a ticket.
 * - Observable. `subscribe` feeds React through `useSyncExternalStore`.
 *
 * It never sends anything anywhere. Shipping logs off-device would be a
 * privacy decision for a legal product, not a frontend one.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEntry {
  /** Monotonic within a session; also the React key. */
  id: number;
  /** Epoch milliseconds. */
  ts: number;
  level: LogLevel;
  /** Subsystem that emitted it: `api`, `auth`, `chat`, `jobs`, `ui`… */
  scope: string;
  message: string;
  /** Structured detail, already redacted. */
  data?: Record<string, unknown>;
  /** The backend's `X-Correlation-Id`, when the entry came from a response. */
  correlationId?: string;
}

const LEVELS: Record<LogLevel, number> = { debug: 10, info: 20, warn: 30, error: 40 };
const BUFFER_SIZE = 400;
const LEVEL_KEY = 'alfie:log-level';

/** Keys whose values are replaced with `[redacted]` before storage. */
const SECRET_KEY = /(pass|secret|token|authorization|cookie|apikey|api_key|credential)/i;

function isDev(): boolean {
  try {
    return Boolean(import.meta.env?.DEV);
  } catch {
    return false;
  }
}

function readStoredLevel(): LogLevel | null {
  try {
    const raw = window.localStorage.getItem(LEVEL_KEY);
    return raw && raw in LEVELS ? (raw as LogLevel) : null;
  } catch {
    return null; // Private mode, or storage disabled — not an error.
  }
}

/** Deep-copies `data`, redacting secrets and truncating anything unbounded. */
export function redact(value: unknown, depth = 0): unknown {
  if (value == null || typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.length > 400 ? `${value.slice(0, 400)}…(${value.length})` : value;
  if (depth >= 4) return '[deep]';
  if (Array.isArray(value)) return value.slice(0, 20).map((v) => redact(v, depth + 1));
  if (value instanceof Error) return { name: value.name, message: value.message };
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>).slice(0, 30)) {
      out[k] = SECRET_KEY.test(k) ? '[redacted]' : redact(v, depth + 1);
    }
    return out;
  }
  return String(value);
}

class LogBuffer {
  private entries: LogEntry[] = [];
  private listeners = new Set<() => void>();
  private seq = 0;
  private snapshotCache: readonly LogEntry[] = [];
  level: LogLevel = readStoredLevel() ?? (isDev() ? 'debug' : 'info');
  /** Mirror entries to the browser console. On in dev, off in production. */
  echo = isDev();

  push(level: LogLevel, scope: string, message: string, data?: Record<string, unknown>, correlationId?: string): void {
    if (LEVELS[level] < LEVELS[this.level]) return;
    const entry: LogEntry = {
      id: ++this.seq,
      ts: Date.now(),
      level,
      scope,
      message,
      ...(data ? { data: redact(data) as Record<string, unknown> } : {}),
      ...(correlationId ? { correlationId } : {}),
    };
    this.entries.push(entry);
    if (this.entries.length > BUFFER_SIZE) this.entries.splice(0, this.entries.length - BUFFER_SIZE);
    this.snapshotCache = this.entries.slice();
    if (this.echo) {
      const fn = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
      fn(`%c${scope}%c ${message}`, 'color:#b03119;font-weight:600', 'color:inherit', entry.data ?? '');
    }
    for (const l of this.listeners) l();
  }

  /** Stable-identity snapshot — required by `useSyncExternalStore`. */
  snapshot(): readonly LogEntry[] {
    return this.snapshotCache;
  }

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  clear(): void {
    this.entries = [];
    this.snapshotCache = [];
    for (const l of this.listeners) l();
  }

  setLevel(level: LogLevel): void {
    this.level = level;
    try {
      window.localStorage.setItem(LEVEL_KEY, level);
    } catch {
      /* storage unavailable — the level still applies for this session */
    }
  }
}

const buffer = new LogBuffer();

export interface Logger {
  debug(message: string, data?: Record<string, unknown>): void;
  info(message: string, data?: Record<string, unknown>): void;
  warn(message: string, data?: Record<string, unknown>): void;
  error(message: string, data?: Record<string, unknown>): void;
  /** A logger bound to a narrower scope: `log.child('chat')`. */
  child(scope: string): Logger;
}

function makeLogger(scope: string): Logger {
  const at = (level: LogLevel) => (message: string, data?: Record<string, unknown>) => {
    // `correlationId` is promoted out of `data` so the console and the
    // Diagnostics table can both key on it without callers thinking about it.
    const cid = typeof data?.correlationId === 'string' ? data.correlationId : undefined;
    buffer.push(level, scope, message, data, cid);
  };
  return {
    debug: at('debug'),
    info: at('info'),
    warn: at('warn'),
    error: at('error'),
    child: (sub: string) => makeLogger(`${scope}:${sub}`),
  };
}

/** The root logger. Prefer a scoped child: `logger.child('drafter')`. */
export const logger = makeLogger('app');

export const logStore = {
  subscribe: buffer.subscribe,
  snapshot: () => buffer.snapshot(),
  clear: () => buffer.clear(),
  setLevel: (l: LogLevel) => buffer.setLevel(l),
  getLevel: () => buffer.level,
  setEcho: (on: boolean) => {
    buffer.echo = on;
  },
};

/** Formats the buffer as plain text for a support ticket. */
export function formatLogs(entries: readonly LogEntry[]): string {
  return entries
    .map((e) => {
      const t = new Date(e.ts).toISOString();
      const cid = e.correlationId ? ` cid=${e.correlationId}` : '';
      const data = e.data && Object.keys(e.data).length ? ` ${JSON.stringify(e.data)}` : '';
      return `${t} ${e.level.toUpperCase().padEnd(5)} ${e.scope}${cid} — ${e.message}${data}`;
    })
    .join('\n');
}
